// Lib
import { useEffect, useRef } from 'react';
import { clone, round } from 'lodash';
import { useSelector } from 'react-redux';

// Selectors
import {
    selectDragOrigin,
    selectGroupLockAspectRatio,
    selectGroupMinScales,
    selectResizeScale,
} from './store/resizingSelector';
import { getCurrentVisibleBoardCanvasOrigin } from '../selectors/currentBoardSelector';

import { analyticsEvent } from '../../analytics';
import measurementsRegistry from '../../components/measurementsStore/measurementsRegistry';

// Hooks
import useUpdateEffect from '../../utils/react/useUpdateEffect';
import useElementResizeHandlers from './useElementResizeHandlers';
import useBecameTrueEffect from '../../utils/react/useBecameTrueEffect';

// Utils
import { asObject, prop } from '../../../common/utils/immutableHelper';
import { getXPosition, getYPosition } from '../../../common/elements/utils/elementPropertyUtils';
import { getDomCanvasElementSelector } from '../utils/elementUtil';
import { getCurrentElementSize } from './resizeUtils';

// Types
import { ImMNElement } from '../../../common/elements/elementModelTypes';
import { ResizeAspectLock, SetElementSizeAndPosition, SetTempElementSize } from './resizeTypes';
import { Point, PointYOptional } from '../../../common/maths/geometry/pointTypes';
import { RectSize } from '../../../common/maths/geometry/rect/rectTypes';

export type ResizeProps = {
    elementId: string;
    getCurrentMeasurements: (elementId: string) => Map<string, number>;
    getContextZoomScale: () => number;
    element: ImMNElement;
    setElementSizeAndPosition: SetElementSizeAndPosition;
    setTempElementSize: SetTempElementSize;
    tempSize: RectSize | null;
    gridSize: number;
    isResizing: boolean;
    resizeAspectLock: ResizeAspectLock;
    minWidth: number;
    minHeight: number;
    allowAspectChangeForCurrentResize: boolean;
    isResizingOrAnimating: boolean;
    onResizeAnimationComplete: () => void;
    getNonMediaHeight: () => number;
};

export type ResizeState = {
    initialElementSize: RectSize | null;
    tempSize: RectSize | null;
    tempPosition: Point | null;
    isResizingOrAnimating: boolean;
    animationTargetSize: RectSize | null;
};

const INITIAL_RESIZE_STATE: ResizeState = {
    initialElementSize: null,
    tempSize: null,
    tempPosition: null,
    isResizingOrAnimating: false,
    animationTargetSize: null,
};

const useGroupResizeBehaviour = (props: ResizeProps, getIsRepositionOnly = (props: ResizeProps) => false) => {
    const {
        element,
        elementId,
        setElementSizeAndPosition,
        getContextZoomScale,
        setTempElementSize,
        tempSize,
        gridSize,
        isResizing,
        isResizingOrAnimating,
        onResizeAnimationComplete,
        minWidth,
        minHeight,
    } = props;

    const resizeStateRef = useRef<ResizeState>(clone(INITIAL_RESIZE_STATE));
    resizeStateRef.current.isResizingOrAnimating = isResizingOrAnimating;

    // Only update while resizing, preserve the value when resizing ends for triggering animations
    if (isResizing) resizeStateRef.current.tempSize = tempSize;

    const domElementRef = useRef<HTMLElement | null>(null);
    const repositionOnly = getIsRepositionOnly(props);
    const zoomScale = getContextZoomScale?.() || 1;
    const groupLockAspectRatio = useSelector(selectGroupLockAspectRatio(elementId));

    const scale = useSelector(selectResizeScale(elementId));
    const scaleX = prop('x', scale);
    const scaleY = groupLockAspectRatio ? scaleX : prop('y', scale);

    const minGroupResizeScale = useSelector(selectGroupMinScales(elementId));
    const minScaleX = prop('x', minGroupResizeScale) || 0;
    const minScaleY = prop('y', minGroupResizeScale) || 0;

    const originImmutable = useSelector(selectDragOrigin(elementId));
    const origin: Point = asObject(originImmutable || {});

    const canvasOrigin = useSelector(getCurrentVisibleBoardCanvasOrigin);
    const initialDistanceFromOrigin = {
        x: getXPosition(element) - origin.x,
        y: getYPosition(element) - origin.y,
    };

    const setTempPosition = (positionGu: PointYOptional) => {
        if (!domElementRef.current) {
            domElementRef.current = document.querySelector(getDomCanvasElementSelector(elementId));
        }

        if (!positionGu.x || !positionGu.y || !domElementRef.current) return;

        resizeStateRef.current.tempPosition = positionGu as Point;

        const leftPx = (positionGu.x - prop('x', canvasOrigin)) * gridSize;
        domElementRef.current?.style.setProperty('left', `${leftPx}px`);

        const topPx = (positionGu.y - prop('y', canvasOrigin)) * gridSize;
        domElementRef.current?.style.setProperty('top', `${topPx}px`);
    };

    const finishResize = () => {
        setTempElementSize(null);
        resizeStateRef.current = clone(INITIAL_RESIZE_STATE);
        onResizeAnimationComplete();
    };

    // Get initial size values within a useEffect to ensure we have the latest values from the store after isResizing becomes true
    useBecameTrueEffect(() => {
        const initialElementSize = getCurrentElementSize(elementId, gridSize);

        resizeStateRef.current.initialElementSize = initialElementSize;

        // Setting the tempSize to the current size on start of resize helps with other size calculations
        // such as the padding for aspect ratio in ResponsiveProgressiveImage
        setTempElementSize(initialElementSize);
    }, isResizing);

    useElementResizeHandlers({
        props,
        resizeStateRef,
        finishResize,
        setTempElementSize,
        setTempPosition,
        repositionOnly,
    });

    useEffect(() => {
        return () => {
            // Ensure that the state gets updated when unmounting
            if (resizeStateRef.current.isResizingOrAnimating) finishResize();
        };
    }, []);

    const getHasPositionChanged = () => {
        const { tempPosition } = resizeStateRef.current;
        const hasXChanged = tempPosition && tempPosition.x !== origin.x + initialDistanceFromOrigin.x;
        const hasYChanged = tempPosition && tempPosition.y !== origin.y + initialDistanceFromOrigin.y;
        return hasXChanged || hasYChanged;
    };

    const getHasSizeChanged = () => {
        const { initialElementSize, tempSize } = resizeStateRef.current;

        if (!initialElementSize || !tempSize) return false;

        return tempSize.width !== initialElementSize.width || tempSize.height !== initialElementSize.height;
    };

    useUpdateEffect(() => {
        const canvasViewportElement = measurementsRegistry.getCanvasViewportDomElement();
        canvasViewportElement.classList.toggle('resizing-element', isResizing);

        if (isResizing) return;

        const hasPositionChanged = getHasPositionChanged();
        const hasSizeChanged = getHasSizeChanged();

        if (!hasPositionChanged && !hasSizeChanged) {
            finishResize();
            return;
        }

        const { tempSize, tempPosition } = resizeStateRef.current;
        setElementSizeAndPosition(clone(tempSize), clone(tempPosition || undefined));

        if (hasSizeChanged) analyticsEvent('resized-element');

        setTempElementSize(null);

        requestAnimationFrame(() => {
            const { tempPosition, tempSize } = resizeStateRef.current;

            // Make sure the temp position is still applied before animating to the rounded position
            if (tempPosition) setTempPosition(tempPosition);

            // Different elements have different ways of snapping or calculating their final size,
            // so take measurement here to see where to animate to
            if (tempSize) {
                resizeStateRef.current.animationTargetSize = getCurrentElementSize(elementId, gridSize);
                setTempElementSize(tempSize);
            }
        });
    }, [isResizing]);

    useEffect(() => {
        if (!scaleX && !scaleY) return;

        const fixedAspectRatioMinScale = Math.max(minScaleX, minScaleY);
        const safeScaleX = Math.max(scaleX, groupLockAspectRatio ? fixedAspectRatioMinScale : minScaleX);
        const safeScaleY = groupLockAspectRatio ? safeScaleX : Math.max(scaleY, minScaleY);

        const { x: distanceX, y: distanceY } = initialDistanceFromOrigin;

        if (distanceX + distanceY > 0) {
            setTempPosition({
                x: origin.x + distanceX * safeScaleX,
                y: origin.y + distanceY * safeScaleY,
            });
        }

        if (repositionOnly) return;

        const { initialElementSize } = resizeStateRef.current;
        if (!initialElementSize) return;

        setTempElementSize({
            width: Math.max(round(initialElementSize.width * safeScaleX, 2), minWidth),
            height: Math.max(round(initialElementSize.height * safeScaleY, 2), minHeight),
        });
    }, [scaleX, scaleY, zoomScale, groupLockAspectRatio]);
};

export default useGroupResizeBehaviour;
